import React from 'react';
import { Heart, MoveHorizontal, ShieldCheck, Clock, MessageCircle, Plus } from 'lucide-react';

const people = [
  { n:'Evelyn', r:'Grandmother', x:90, y:70, s:'mom' }, { n:'James', r:'Grandfather', x:250, y:70, s:'dad' },
  { n:'Denise', r:'Aunt / Uncle', x:-70, y:190, s:'mom' }, { n:'Lisa', r:'Mother', x:130, y:190, s:'mom' }, { n:'Marcus', r:'Father', x:310, y:190, s:'dad' }, { n:'Andre', r:'Uncle', x:500, y:190, s:'dad' },
  { n:'Maya', r:'First cousin', x:-90, y:330, s:'mom' }, { n:'You', r:'Center of tree', x:190, y:330, s:'self', you:true }, { n:'Jordan', r:'Spouse', x:370, y:330, s:'shared' }, { n:'Caleb', r:'First cousin', x:570, y:330, s:'dad' },
  { n:'Ava', r:'Daughter', x:130, y:480, s:'shared' }, { n:'Elijah', r:'Son', x:300, y:480, s:'shared' }, { n:'Noelle', r:'Step-child', x:470, y:480, s:'shared' },
];

const ring = (s:string) => s === 'mom' ? 'from-pink-400 to-fuchsia-500' : s === 'dad' ? 'from-sky-400 to-cyan-500' : s === 'self' ? 'from-fuchsia-600 to-purple-700' : 'from-purple-400 to-pink-400';

function Card({p}:{p:any}) {
  return (
    <div className="absolute w-36 h-20 rounded-2xl bg-white shadow-md border border-slate-100 px-3 py-2" style={{left:p.x, top:p.y}}>
      <div className="flex items-center gap-2">
        <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${ring(p.s)} flex items-center justify-center text-white shadow`}>
          <span className="text-lg">👤</span>
        </div>
        <div className="min-w-0">
          {p.you && <div className="text-[9px] font-bold text-fuchsia-600">YOU</div>}
          <div className="text-xs font-bold text-slate-900 truncate">{p.n}</div>
          <div className="text-[10px] font-semibold text-purple-600 truncate">{p.r}</div>
        </div>
      </div>
      <MessageCircle className="absolute -left-1 bottom-2 w-5 h-5 text-sky-500 bg-white rounded-full" />
      <Plus className="absolute -right-2 bottom-2 w-5 h-5 text-white bg-fuchsia-500 rounded-full p-1" />
    </div>
  );
}

const TreePreview: React.FC = () => (
  <section className="bg-gradient-to-b from-pink-50/40 via-white to-purple-50/40 py-20 sm:py-24">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-2xl mx-auto mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-fuchsia-600 font-semibold mb-3">Your living family tree</div>
        <h2 className="font-serif text-4xl md:text-5xl font-bold text-slate-900 leading-tight">Slide through your whole family circle.</h2>
        <p className="mt-5 text-lg text-slate-600">Mother’s side, father’s side, spouses, aunts, uncles, cousins, children, and blended branches stay clear and connected.</p>
      </div>

      <div className="mx-auto mb-4 w-fit flex items-center gap-2 rounded-full bg-white border border-fuchsia-100 px-4 py-2 text-sm font-semibold text-fuchsia-700 shadow-sm">
        <MoveHorizontal className="w-4 h-4" /> Slide left/right to explore branches
      </div>

      <div className="relative rounded-3xl border border-fuchsia-100 bg-gradient-to-b from-white via-fuchsia-50/40 to-purple-50/60 shadow-xl overflow-hidden">
        <div className="overflow-x-auto px-5 py-8 scroll-smooth">
          <div className="relative min-w-[760px] h-[610px]">
            <div className="absolute inset-0 opacity-[0.06]" style={{backgroundImage:'linear-gradient(to right,#a855f7 1px,transparent 1px),linear-gradient(to bottom,#a855f7 1px,transparent 1px)',backgroundSize:'42px 42px'}} />
            {['GRANDPARENTS','PARENTS','YOU + SPOUSE','CHILDREN'].map((l,i)=>(
              <div key={l} className="absolute left-0 right-0 h-24 rounded-full bg-white/40" style={{top:40+i*135}}>
                <span className="absolute left-3 top-2 rounded-full bg-white px-3 py-1 text-[10px] font-bold tracking-[0.2em] text-slate-500">{l}</span>
              </div>
            ))}
            <svg className="absolute inset-0 w-full h-full pointer-events-none">
              <path d="M160 150 C160 190 190 205 200 250" stroke="#ec4899" strokeWidth="2" fill="none" opacity=".55"/>
              <path d="M320 150 C320 190 310 205 300 250" stroke="#38bdf8" strokeWidth="2" fill="none" opacity=".55"/>
              <path d="M200 270 C210 320 240 330 260 355" stroke="#ec4899" strokeWidth="2" fill="none" opacity=".55"/>
              <path d="M380 270 C360 320 330 330 320 355" stroke="#38bdf8" strokeWidth="2" fill="none" opacity=".55"/>
              <path d="M260 410 C240 455 210 470 190 500" stroke="#a855f7" strokeWidth="2" fill="none" opacity=".45"/>
              <path d="M360 410 C380 455 420 470 455 500" stroke="#a855f7" strokeWidth="2" fill="none" opacity=".45"/>
            </svg>
            <div className="absolute left-[340px] top-[370px] w-12 h-px bg-gradient-to-r from-fuchsia-300 to-purple-300" />
            <div className="absolute left-[342px] top-[356px] w-8 h-8 rounded-full bg-white border-2 border-rose-300 shadow flex items-center justify-center">
              <Heart className="w-4 h-4 text-rose-500 fill-rose-500" />
            </div>
            {people.map((p)=><Card key={p.n} p={p}/>)}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 bg-white/75 border-t border-slate-100 px-4 py-3 text-[11px] text-slate-600">
          <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-emerald-600"/> Verified</span>
          <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-amber-600"/> Pending</span>
          <span className="flex items-center gap-1"><Heart className="w-3 h-3 text-rose-500 fill-rose-500"/> Spouse link</span>
          <span>Tap, slide, and explore the family branches</span>
        </div>
      </div>
    </div>
  
      <div data-landing-only="preserve-memories-profile-demo" className="mt-8 rounded-3xl border border-fuchsia-100 bg-white shadow-xl overflow-hidden">
        <div className="bg-gradient-to-br from-fuchsia-500 via-pink-500 to-purple-500 px-5 py-6 text-white">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-white/20 border-4 border-white shadow-lg overflow-hidden flex items-center justify-center text-3xl">🌳</div>
            <div>
              <div className="font-serif text-2xl font-bold">Open a family profile</div>
              <div className="text-white/90 text-sm">See how every person becomes part of the story.</div>
              <div className="mt-2 inline-flex rounded-full bg-white/20 px-3 py-1 text-xs font-semibold">Sample member card</div>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3 font-semibold text-emerald-700">📞<br/>Call</div>
            <div className="rounded-2xl border border-purple-200 bg-purple-50 p-3 font-semibold text-purple-700">🎥<br/>Video</div>
            <div className="rounded-2xl border border-sky-200 bg-sky-50 p-3 font-semibold text-sky-700">💬<br/>Message</div>
          </div>

          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="rounded-2xl border border-fuchsia-200 bg-fuchsia-50 p-3 font-semibold text-fuchsia-700">📷<br/>Photo</div>
            <div className="rounded-2xl border border-pink-200 bg-pink-50 p-3 font-semibold text-pink-700">🎬<br/>Video</div>
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 font-semibold text-amber-700">✍️<br/>Story</div>
          </div>

          <div className="rounded-2xl bg-gradient-to-r from-purple-600 via-pink-500 to-amber-400 p-4 text-white text-center font-bold shadow-md">
            Sign up to preserve your family memories
          </div>
        </div>
      </div>

    </section>
);

export default TreePreview;
